import { Navigate, Route, Routes } from 'react-router-dom'
import { Suspense, lazy } from 'react'
import { useUser } from '@/store'
import { links } from './links'
import { Spinner } from '@heroui/react'

const Connect = lazy(() => import('./connect').then((module) => ({ default: module.Connect })))
const Signup = lazy(() => import('./signup').then((module) => ({ default: module.Signup })))
const Login = lazy(() => import('./login').then((module) => ({ default: module.Login })))
const SignupSuccess = lazy(() =>
  import('./signup-success').then((module) => ({ default: module.SignupSuccess })),
)
const SignupVerify = lazy(() =>
  import('./verify-signup').then((module) => ({ default: module.SignupVerify })),
)
const Dashboard = lazy(() =>
  import('./dashboard').then((module) => ({ default: module.Dashboard })),
)
const ForgotPassword = lazy(() =>
  import('./forgot-password').then((module) => ({ default: module.ForgotPassword })),
)
const ResetPassword = lazy(() =>
  import('./reset-password').then((module) => ({ default: module.ResetPassword })),
)

const RouterGuard = ({ children }: { children: JSX.Element }) => {
  const token = useUser((state) => state.token)

  if (token) return children
  console.log('[RouterGuard] No token found')
  return <Navigate to={links.connect.root} />
}

const AuthGuard = ({ children }: { children: JSX.Element }) => {
  const token = useUser((state) => state.token)

  if (token) {
    console.log('[AuthGuard] User is authenticated, redirecting to dashboard')
    return <Navigate to={links.dashboard.root} />
  }
  return children
}

export const MainRouter = () => {
  return (
    <Suspense
      fallback={
        <div className='w-full h-screen flex items-center justify-center'>
          <Spinner className='w-8 h-8' />
        </div>
      }
    >
      <Routes>
        <Route
          path={links.connect.root}
          element={
            <AuthGuard>
              <Connect />
            </AuthGuard>
          }
        />
        <Route
          path={links.signup.root}
          element={
            <AuthGuard>
              <Signup />
            </AuthGuard>
          }
        />
        <Route
          path={links.signup.success}
          element={
            <AuthGuard>
              <SignupSuccess />
            </AuthGuard>
          }
        />
        <Route path={links.signup.verify} element={<SignupVerify />} />
        <Route
          path={links.login.root}
          element={
            <AuthGuard>
              <Login />
            </AuthGuard>
          }
        />
        <Route
          path={links.dashboard.root}
          element={
            <RouterGuard>
              <Dashboard />
            </RouterGuard>
          }
        />
        <Route path={links.login.forgotPassword} element={<ForgotPassword />} />
        <Route path={links.login.resetPassword} element={<ResetPassword />} />
        <Route path={'*'} element={<Navigate to={links.connect.root} />} />
      </Routes>
    </Suspense>
  )
}
